import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, H2, P, PageHero, Section } from "@/components/SharedUI";
import Term, { TERMS } from "@/components/Term";
import SpinySphereExplorer from "@/components/SpinySphereExplorer";

const architectureLayers = [
  [
    "Advisory Diagnostics Suite™",
    "Risk, gap, readiness and operating environment review before major investment or implementation.",
    "A scored readiness report, a gap analysis and a risk profile your executive can use to approve or pause the project.",
  ],
  [
    "Governance Architecture Suite™",
    "Decision rights, role matrices, phase gates, assurance pathways and executive operating model.",
    "A complete decision-rights model — who decides what, who signs off, and when reviews happen.",
  ],
  [
    "Dreaming Architecture™ Visual Systems",
    "Visual method for translating complex governance and systems logic into boardroom-ready architecture.",
    "Clear boardroom-ready diagrams of governance logic, suitable for executives and councils.",
  ],
  [
    "ICIP Protection & Permissions Suite™",
    "Sharing rules, consent pathways, approval registers, takedown process and use conditions.",
    "An operational set of sharing rules, consent records and takedown processes for every cultural asset.",
  ],
  [
    "Business Analysis Toolkits™",
    "Current-state maps, future-state models, stakeholder maps, requirements matrices and workflow architecture.",
    "Current-state maps, future-state designs and stakeholder analysis ready to feed into your delivery team.",
  ],
  [
    "Protected Delivery Pathways™",
    "Implementation planning, pilot design, partner delivery and protected infrastructure staging.",
    "A staged implementation plan and a controlled pilot environment, with no public exposure until you're ready.",
  ],
  [
    "BIGIBILA SYSTEMS™ Platform",
    "Software layer for permissions, authority mapping, registers, escalation, evidence and governance dashboarding.",
    "Working software that holds permissions, registers, escalations and audit evidence in one protected place.",
  ],
];

export default function Architecture() {
  return (
    <>
      <PageHero
        eyebrow="Governance architecture model"
        title={<><Term def={TERMS.LoreLed}>Lore-Led Architecture™</Term> turns story, responsibility and authority into practical operating design.</>}
        desc={<>The architecture stack translates complex cultural, organisational and delivery environments into structured products, methods and implementation pathways. <Term def={TERMS.Lore}>Lore</Term> stays at the centre — software answers to it.</>}
      />

      {/* Bigibila Spiny Sphere — official architecture diagram (interactive) */}
      <section className="border-b border-stone-200">
        <Section>
          <div className="max-w-3xl mb-12">
            <Eyebrow>Bigibila Spiny Sphere™</Eyebrow>
            <H2 className="text-3xl md:text-4xl">A living architecture — designed by Country, governed by Lore.</H2>
            <P className="mt-6">
              The Spiny Sphere holds sacred knowledge and cultural authority at the centre, surrounded by concentric rings of <span className="font-semibold text-stone-900">Lore, Governance, Community, Permissions, Metadata</span> and <span className="font-semibold text-stone-900">Systems</span>. External spines — <Term def={TERMS.FPIC}>FPIC</Term>, <Term def={TERMS.ICIP}>ICIP</Term>, <Term def={TERMS.IDS}>IDS</Term>, Escalation and Access Control — are the protective extensions that connect, enforce and uphold the architecture. Tap any spine on the diagram to see how it works in practice.
            </P>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-200 border border-stone-200 max-w-2xl">
              {[
                ["Designed by", "Country"],
                ["Governed by", "Lore"],
                ["Protected by", "Authority"],
                ["Sustained by", "Community"],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#FAFAF9] p-4">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">{k}</div>
                  <div className="mt-1 font-display font-bold text-stone-900">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <SpinySphereExplorer />

          <p className="mt-10 text-stone-700 italic leading-relaxed border-l-2 border-[#9C4221]/40 pl-4 max-w-2xl">
            "Not just data. Not just systems. A living expression of Indigenous self-determination."
          </p>
        </Section>
      </section>

      <section className="border-b border-stone-200">
        <Section>
          <div className="grid gap-px bg-stone-200 border border-stone-200">
            {architectureLayers.map(([title, desc, outcome], i) => (
              <div
                key={title}
                data-testid={`arch-layer-${i}`}
                className="bg-[#FAFAF9] p-7 grid grid-cols-1 md:grid-cols-12 gap-6 items-start"
              >
                <div className="md:col-span-1">
                  <div className="font-display text-3xl tracking-tight text-stone-300 font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-display text-2xl tracking-tight font-bold text-stone-900">{title}</h3>
                  <p className="mt-3 text-stone-600 leading-relaxed">{desc}</p>
                </div>
                <div className="md:col-span-6 md:border-l md:border-stone-200 md:pl-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-2">What you get</div>
                  <p className="text-stone-800 leading-relaxed">{outcome}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      <section className="border-b border-stone-200">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Eyebrow>The quills are the governance logic</Eyebrow>
              <H2 className="text-3xl md:text-4xl">From story to system.</H2>
              <P className="mt-6">
                The quills become controls. The story becomes method. The responsibility becomes a system of permission, consent, evidence, accountability and protection.
              </P>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
              {[
                ["Protection", "Systems must protect before harm occurs."],
                ["Boundary", "Authority, permission and cultural restrictions must be respected."],
                ["Consequence", "When obligations are broken, accountability must follow."],
                ["Survival", "Harm can be transformed into protective architecture."],
              ].map(([title, desc]) => (
                <div key={title} className="bg-[#FAFAF9] p-6">
                  <h3 className="font-display font-bold text-lg tracking-tight text-[#9C4221]">{title}</h3>
                  <p className="mt-2 text-sm text-stone-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* Governance Archaeology research callout */}
      <section className="border-b border-stone-200 bg-[#FAFAF9]">
        <Section>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <Eyebrow>Bigibila Research · A service offer</Eyebrow>
              <H2 className="text-3xl md:text-4xl">Governance Archaeology.</H2>
              <P className="mt-6">
                A research project asking whether First Nations governance systems can be identified through public evidence — sacred places, Lore, ceremony, kinship, oral traditions, colonial archives and contemporary Indigenous governance evidence — mapped into a First Nations Governance Architecture.
              </P>
              <p className="mt-3 text-sm text-stone-500 italic">
                Public evidence only. Cultural authority remains with Traditional Owners, Elders, families and Nations.
              </p>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <Link
                to="/research"
                data-testid="architecture-research-link"
                className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
              >
                See the research <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>
    </>
  );
}
